import type {
  AddUserRoleAssignmentRequestParams,
  GetUserRoleAssignmentsRequestParams,
  RemoveUserRoleAssignmentRequestParams,
  UserRoleAssignment
} from '@gat-ship/types'
import {
  userRoleAssignmentsKey,
  type UserRoleAssignmentsService
} from '@gat-ship/types/injection-keys'
import { useAccessControlSignalREvents } from '@gat-ship/signalr'
import { inject, onBeforeUnmount, ref, shallowRef } from 'vue'
import {
  appendRoleAssignment,
  removeRoleAssignmentById
} from '../model/user-role-assignment-collection'
import { useAccessControlApiCalls } from './useAccessControlApiCalls'

function defineTenantUserRoleAssignmentState() {
  const roleAssignments = shallowRef<UserRoleAssignment[]>([])
  function setRoleAssignments(data: UserRoleAssignment[]) {
    roleAssignments.value = data
  }

  const { getUserRoleAssignmentsRequest } = useAccessControlApiCalls()

  const roleAssignmentsIsFetched = ref(false)

  /**
   * Fetch the role assignments for the tenant user.
   * @param params - The parameters for the request.
   * @param params.forceFetch - Whether to force a fetch of the role assignments. @default false
   * @param params.subscribeToEvents - Whether to subscribe to the role assignment events. @default true
   * @returns The role assignments for the tenant user.
   */
  async function fetchRoleAssignments(
    params: GetUserRoleAssignmentsRequestParams & {
      forceFetch?: boolean
      subscribeToEvents?: boolean
    }
  ) {
    if (!params.forceFetch && roleAssignmentsIsFetched.value) {
      return
    }

    const result = await getUserRoleAssignmentsRequest(params)
    if (result) {
      setRoleAssignments(result.roleAssignments)
      roleAssignmentsIsFetched.value = true
    }
    if (params.subscribeToEvents ?? true) {
      subscribeToRoleAssignmentEvents()
    }
  }

  const signalR = useAccessControlSignalREvents({
    onUserRoleAssignmentAdded: (payload) => {
      setRoleAssignments(
        appendRoleAssignment(roleAssignments.value, {
          roleAssignmentId: payload.message.roleAssignmentId,
          roleId: payload.message.roleId,
          roleName: payload.message.roleName,
          agencyNodeId: payload.message.agencyNodeId,
          agencyNodeName: payload.message.agencyNodeName,
          userId: payload.message.userId
        })
      )
    },
    onUserRoleAssignmentRemoved: (payload) => {
      setRoleAssignments(
        removeRoleAssignmentById(
          roleAssignments.value,
          payload.message.roleAssignmentId
        )
      )
    }
  })

  function subscribeToRoleAssignmentEvents() {
    signalR.subscribeToAccessControlEvents()
    return () => {
      signalR.unsubscribeFromAccessControlEvents()
    }
  }

  function unsubscribeFromRoleAssignmentEvents() {
    signalR.unsubscribeFromAccessControlEvents()
  }
  return {
    roleAssignments,
    set: setRoleAssignments,
    fetch: fetchRoleAssignments,
    unsubscribeFromRoleAssignmentEvents
  }
}

function defineTenantUserRoleAssignmentCommands(
  roleAssignmentState: ReturnType<typeof defineTenantUserRoleAssignmentState>
) {
  const { removeUserRoleAssignmentRequest, addUserRoleAssignmentRequest } =
    useAccessControlApiCalls()

  async function addRoleAssignment(params: AddUserRoleAssignmentRequestParams) {
    const response = await addUserRoleAssignmentRequest(params)
    await roleAssignmentState.fetch({
      userId: params.userId,
      forceFetch: true
    })
    return response
  }

  async function removeRoleAssignment(
    params: RemoveUserRoleAssignmentRequestParams
  ) {
    const response = await removeUserRoleAssignmentRequest(params)
    await roleAssignmentState.fetch({
      userId: params.userId,
      forceFetch: true
    })
    return response
  }

  return {
    addRoleAssignment,
    removeRoleAssignment
  }
}

/**
 * Datasource for a single user's role assignments. Built on the
 * access-control api/signalR, folded here from the tenant-users sub-domain.
 */
export function useUserRoleAssignments(): UserRoleAssignmentsService {
  const roleAssingnmentState = defineTenantUserRoleAssignmentState()

  const commands = defineTenantUserRoleAssignmentCommands(roleAssingnmentState)

  onBeforeUnmount(() => {
    roleAssingnmentState.unsubscribeFromRoleAssignmentEvents()
  })

  return {
    roleAssignments: roleAssingnmentState.roleAssignments,
    fetchRoleAssignments: roleAssingnmentState.fetch,
    commands
  }
}

/** Inject the user-role-assignments context provided by a parent provider. */
export function useUserRoleAssignmentsContext() {
  return inject(userRoleAssignmentsKey, undefined)
}
